export interface BookingSeriesPattern {
  weekdays: number[] // (0 = Sun … 6 = Sat)
  startTime: string // 'HH:MM'
  durationMin: number
  fromDate: string // 'YYYY-MM-DD'
  toDate: string // 'YYYY-MM-DD'
}
export type EduSeriesPattern = BookingSeriesPattern

export interface BookingHoliday {
  from: string // 'YYYY-MM-DD'
  to: string // 'YYYY-MM-DD'
  label?: string
}
export type EduHoliday = BookingHoliday

export interface BookingClashSlot {
  id?: string
  seriesId?: string
  weekdays: number[]
  startTime: string // 'HH:MM'
  durationMin: number
  fromDate: string // 'YYYY-MM-DD'
  toDate: string // 'YYYY-MM-DD'
  teacherUids?: string[]
  roomId?: string
  [key: string]: unknown
}
export type EduClashSlot = BookingClashSlot

export interface BookingClash {
  slotA: BookingClashSlot
  slotB: BookingClashSlot
  reason: 'TEACHER' | 'ROOM'
  teacherUid?: string
  roomId?: string
}
export type EduClash = BookingClash

export interface ValidatePatternResult {
  valid: boolean
  errors: string[]
}

function parseUtcDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

function formatUtcDate(date: Date): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getUtcWeekday(dateStr: string): number {
  return parseUtcDate(dateStr).getUTCDay()
}

function daysBetweenUtc(fromStr: string, toStr: string): number {
  const fromMs = parseUtcDate(fromStr).getTime()
  const toMs = parseUtcDate(toStr).getTime()
  return Math.round((toMs - fromMs) / (24 * 60 * 60 * 1000))
}

function addDaysUtc(dateStr: string, days: number): string {
  const dt = parseUtcDate(dateStr)
  dt.setUTCDate(dt.getUTCDate() + days)
  return formatUtcDate(dt)
}

export function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(':').map(Number)
  return h * 60 + m
}

export function sessionKey(seriesId: string, date: string): string {
  const yyyymmdd = date.replace(/-/g, '')
  return `${seriesId}_${yyyymmdd}`
}

export function bookingSessionDocId(bookableId: string, date: string): string {
  return sessionKey(bookableId, date)
}

export function isOccurrence(
  pattern: BookingSeriesPattern,
  date: string,
  holidays: BookingHoliday[] = [],
): boolean {
  if (date < pattern.fromDate || date > pattern.toDate) {
    return false
  }

  const weekday = getUtcWeekday(date)
  if (!pattern.weekdays.includes(weekday)) {
    return false
  }

  for (const h of holidays) {
    if (h.from <= date && date <= h.to) {
      return false
    }
  }

  return true
}

export function expandOccurrences(
  pattern: BookingSeriesPattern,
  holidays: BookingHoliday[] = [],
  windowFrom: string,
  windowTo: string,
): string[] {
  if (windowFrom > windowTo) {
    return []
  }

  let effectiveWindowTo = windowTo
  const windowDays = daysBetweenUtc(windowFrom, windowTo)
  if (windowDays > 120) {
    effectiveWindowTo = addDaysUtc(windowFrom, 120)
  }

  const start = pattern.fromDate > windowFrom ? pattern.fromDate : windowFrom
  const end = pattern.toDate < effectiveWindowTo ? pattern.toDate : effectiveWindowTo

  if (start > end) {
    return []
  }

  const result: string[] = []
  let curr = start
  while (curr <= end) {
    if (isOccurrence(pattern, curr, holidays)) {
      result.push(curr)
    }
    curr = addDaysUtc(curr, 1)
  }

  return result
}

export function findClashes(slots: BookingClashSlot[]): BookingClash[] {
  const clashes: BookingClash[] = []

  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i]
      const b = slots[j]

      const hasSharedWeekday = a.weekdays.some((w) => b.weekdays.includes(w))
      if (!hasSharedWeekday) continue

      const maxFrom = a.fromDate > b.fromDate ? a.fromDate : b.fromDate
      const minTo = a.toDate < b.toDate ? a.toDate : b.toDate
      if (maxFrom > minTo) continue

      const aStart = timeToMinutes(a.startTime)
      const aEnd = aStart + a.durationMin
      const bStart = timeToMinutes(b.startTime)
      const bEnd = bStart + b.durationMin

      const maxStart = Math.max(aStart, bStart)
      const minEnd = Math.min(aEnd, bEnd)
      if (maxStart >= minEnd) continue

      const aTeachers = a.teacherUids || []
      const bTeachers = b.teacherUids || []
      const sharedTeachers = aTeachers.filter((t) => bTeachers.includes(t))
      for (const t of sharedTeachers) {
        clashes.push({
          slotA: a,
          slotB: b,
          reason: 'TEACHER',
          teacherUid: t,
        })
      }

      if (a.roomId && b.roomId && a.roomId === b.roomId) {
        clashes.push({
          slotA: a,
          slotB: b,
          reason: 'ROOM',
          roomId: a.roomId,
        })
      }
    }
  }

  return clashes
}

export function isValidIsoDate(s: unknown): s is string {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return false
  }
  const [yearStr, monthStr, dayStr] = s.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  const d = new Date(Date.UTC(year, month - 1, day))
  if (Number.isNaN(d.getTime())) {
    return false
  }
  return d.toISOString().slice(0, 10) === s
}

export function validatePattern(pattern: BookingSeriesPattern): ValidatePatternResult {
  const errors: string[] = []

  if (
    !Array.isArray(pattern.weekdays) ||
    pattern.weekdays.length === 0 ||
    pattern.weekdays.some((w) => typeof w !== 'number' || w < 0 || w > 6 || !Number.isInteger(w)) ||
    new Set(pattern.weekdays).size !== pattern.weekdays.length
  ) {
    errors.push('INVALID_WEEKDAYS')
  }

  if (
    typeof pattern.startTime !== 'string' ||
    !/^([01]\d|2[0-3]):[0-5]\d$/.test(pattern.startTime)
  ) {
    errors.push('INVALID_START_TIME')
  }

  if (
    typeof pattern.durationMin !== 'number' ||
    !Number.isFinite(pattern.durationMin) ||
    pattern.durationMin < 5 ||
    pattern.durationMin > 480
  ) {
    errors.push('INVALID_DURATION')
  }

  if (
    !isValidIsoDate(pattern.fromDate) ||
    !isValidIsoDate(pattern.toDate)
  ) {
    errors.push('INVALID_PATTERN')
  } else if (pattern.fromDate > pattern.toDate) {
    errors.push('INVALID_DATE_RANGE')
  } else {
    const span = daysBetweenUtc(pattern.fromDate, pattern.toDate)
    if (span > 400) {
      errors.push('SPAN_EXCEEDS_MAX')
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
